import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, pipe } from 'rxjs';
import { map, catchError, retryWhen, delay, take, tap, finalize } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { AuthenticationCheckService } from '../auth/authentication-check.service';
import { ToastClientService } from '../toast/toast-client.service';
import { ActivatedRoute, Router } from '@angular/router';

/**
 * Pre-login API patterns that should use X-Program-Id: master_admin
 * These APIs are called before user selects a specific program
 * Note: admin/program-login is excluded - it uses custom X-Program-Id header set in API service
 */
const PRE_LOGIN_APIS = ['admin/get-programs', 'login/login'];

/**
 * APIs that should not have program_id automatically added by interceptor
 * These handle program_id in their own payload/params
 */
const EXCLUDE_PROGRAM_ID_APIS = [...PRE_LOGIN_APIS, 'admin/program-login'];

/**
 * Message fragments that mean the session is no longer valid.
 * Backend may wrap these in a 422 with the real 401 nested under `data`.
 */
const SESSION_EXPIRED_MESSAGES = [
  'unauthorized',
  'token has expired',
  'no access to this module',
  'access token is not set permission denied!',
  'something wrong with request.',
  'trying to access array offset on value of type bool',
];

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  /**
   * Guards against duplicate logout toasts when several parallel requests
   * all fail with an expired token on the same page.
   */
  private isHandlingSessionExpiry = false;
  /**
   * HubAdmin HTTP Interceptor
   *
   * X-Program-Id Header Logic:
   * - Pre-login APIs (get-programs, login): X-Program-Id: master_admin
   * - Post-login APIs: X-Program-Id: {program_uid} from localStorage
   *
   * Also adds program_id to query params (GET/DELETE) and request body (POST/PUT/PATCH)
   */
  constructor(
    private auth: AuthenticationCheckService,
    private toast: ToastClientService,
    private router: Router
  ) {}
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let disableloader = request.headers.get('skip');

    // For get-programs API, always use master token (original login token)
    // For other APIs, use current auth token (device_token after program selection)
    const isProgramsListApi = request.url.includes('admin/get-programs');
    let token = isProgramsListApi ? this.auth.getMasterToken() : this.auth.getAuthToken();

    // Get program UID from localStorage for X-Program-Id header
    // Always use program_id=1 for query params/body (static)
    const programId = 1; // Static program_id for all APIs
    let programUid: string | null = null;
    try {
      const programData = JSON.parse(localStorage.getItem('program') || '{}');
      programUid = programData.uid || programData.program_uid || null;
    } catch (error) {
      console.warn('Failed to parse program data from localStorage:', error);
    }

    // Determine X-Program-Id header value
    const isPreLoginApi = PRE_LOGIN_APIS.some(api => request.url.includes(api));

    // Check if X-Program-Id header is already set (e.g., by API service for program-login)
    const hasCustomProgramId = request.headers.has('X-Program-Id');

    // Use existing header if present, otherwise determine based on login state
    const xProgramId = hasCustomProgramId
      ? request.headers.get('X-Program-Id')!
      : isPreLoginApi
        ? 'master_admin'
        : programUid || 'master_admin';

    // Add Authorization token with Bearer prefix if available
    if (token) {
      // Trim any whitespace/control characters and add Bearer prefix
      const cleanToken = token.trim();
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${cleanToken}`,
        },
      });
    }

    // Add X-Program-Id header to all requests (only if not already present)
    if (!hasCustomProgramId) {
      request = request.clone({
        setHeaders: {
          'X-Program-Id': xProgramId,
        },
      });
    }

    // Add program_id=1 to all requests (skip for excluded APIs)
    const shouldExcludeProgramId = EXCLUDE_PROGRAM_ID_APIS.some(api => request.url.includes(api));

    if (!shouldExcludeProgramId) {
      // For GET requests: Add/override program_id=1 to URL query parameters
      if (request.method === 'GET') {
        try {
          // Check if program_id is already in request.params (HttpParams)
          const hasParamsProgramId = request.params.has('program_id');
          const paramsProgramId = hasParamsProgramId ? request.params.get('program_id') : null;
          
          // Parse URL - handle both relative and absolute URLs
          let urlObj: URL;
          if (request.url.startsWith('http://') || request.url.startsWith('https://')) {
            // Absolute URL
            urlObj = new URL(request.url);
          } else {
            // Relative URL - use a dummy base
            urlObj = new URL(request.url, 'http://dummy');
          }
          
          // Check current program_id value in URL query string
          const urlProgramId = urlObj.searchParams.get('program_id');
          
          // Determine the effective program_id (from params or URL)
          const effectiveProgramId = paramsProgramId || urlProgramId;
          
          // Only modify if program_id is missing or not equal to '1'
          if (effectiveProgramId !== programId.toString()) {
            // If program_id is in request.params, update it there
            if (hasParamsProgramId) {
              const newParams = request.params.set('program_id', programId.toString());
              request = request.clone({ params: newParams });
            } else {
              // Otherwise add to URL query string
              urlObj.searchParams.set('program_id', programId.toString());
              
              // Reconstruct the URL
              let finalUrl: string;
              if (request.url.startsWith('http://') || request.url.startsWith('https://')) {
                // Return absolute URL
                finalUrl = urlObj.toString();
              } else {
                // Return relative URL (pathname + search + hash)
                finalUrl = urlObj.pathname + urlObj.search + urlObj.hash;
              }
              
              request = request.clone({ url: finalUrl });
            }
          }
          // If program_id is already '1', leave the request unchanged
        } catch (e) {
          // If URL parsing fails, skip program_id injection
          console.warn('Failed to parse URL for program_id injection:', request.url, e);
        }
      }

      // For POST/PUT/PATCH/DELETE requests: Add program_id: 1 to payload
      if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(request.method) && request.body) {
        // Only add if body is an object and doesn't already have program_id
        if (typeof request.body === 'object' && !Array.isArray(request.body)) {
          // Handle FormData separately
          if (request.body instanceof FormData) {
            // Only add if program_id is not already in FormData
            if (!request.body.has('program_id')) {
              const newFormData = new FormData();
              // Copy existing data
              request.body.forEach((value, key) => {
                newFormData.append(key, value);
              });
              // Add program_id
              newFormData.append('program_id', programId.toString());
              request = request.clone({ body: newFormData });
            }
          } else if (!('program_id' in request.body)) {
            // Regular JSON body
            request = request.clone({
              body: { ...request.body, program_id: programId },
            });
          }
        }
      }
    }

    if (!request.headers.has('Content-Type')) {
      request = request.clone({
        setHeaders: {
          // 'content-type': 'application/json'
        },
      });
    }

    request = request.clone({
      headers: request.headers.set('Accept', 'application/json'),
    });
    if (disableloader != 'true') {
      this.loaderStart();
    }

    if (request.url.includes('is_loader=yes')) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      // retryWhen(err=>{
      //     let retries =1;
      //     return err.pipe(
      //         delay(1000),
      //         tap(()=>{
      //
      //         }),
      //         map(error=>{
      //             if(retries++ === 3){
      //                 return error
      //             }
      //             return error
      //         })
      //     )
      // }),
      catchError((error: HttpErrorResponse) => {
        let errorMessage = '';
        let toasterMessage = '';
        if (error.error instanceof ErrorEvent) {
          // client-side error
          errorMessage = `Error: ${error.error.message}`;
          toasterMessage = error.error.message;
        } else {
          // server-side error - extract message with multiple fallback paths
          toasterMessage =
            error.error?.data?.message || // Path 1: {data: {message: "..."}}
            error.error?.message || // Path 2: {message: "..."}
            error.error?.data?.error || // Path 3: {data: {error: "..."}}
            error.error?.error || // Path 4: {error: "..."}
            error.error?.data?.errors?.[0] || // Path 5: {data: {errors: ["..."]}}
            error.error?.errors?.[0] || // Path 6: {errors: ["..."]}
            error.statusText || // Path 7: HTTP status text
            error.message || // Path 8: Error message
            'An error occurred'; // Final fallback

          // If we got an object instead of a string, try to extract message from it
          if (typeof toasterMessage === 'object' && toasterMessage !== null) {
            toasterMessage = (toasterMessage as any).message || JSON.stringify(toasterMessage);
          }

          errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
        }

        console.log('Interceptor - Error:', errorMessage);
        console.log('Interceptor - Toast Message:', toasterMessage);

        // Clean up message - remove extra quotes if present
        // Backend sometimes sends: "\"Required fields missing: Outlet Code\""
        if (typeof toasterMessage === 'string') {
          // Remove leading/trailing quotes and unescape
          toasterMessage = toasterMessage.replace(/^["']|["']$/g, '').trim();
          // Handle escaped quotes
          toasterMessage = toasterMessage.replace(/\\"/g, '"');
        }

        // Check if component wants to handle error toast itself
        // Components can add header: { 'X-Skip-Error-Toast': 'true' }
        const skipErrorToast = request.headers.get('X-Skip-Error-Toast') === 'true';

        try {
          const normalizedMessage = toasterMessage.toLowerCase().replace(/['"]/g, '').trim();

          if (this.isSessionExpired(error, normalizedMessage)) {
            this.handleSessionExpiry();
          } else if (!skipErrorToast) {
            // For all other errors, show toast by default (unless component opts out)
            this.toast.Error(toasterMessage);
          }
        } catch (error) {
          console.error('Error in interceptor error handler:', error);
        }

        this.loaderDismiss();
        // RxJS 6: throwError(value). (RxJS 7+ factory form would emit the function itself here.)
        return throwError(error);
      }),
      finalize(() => {
        this.loaderDismiss();
      })
    );
  }

  /**
   * The API may report an expired token as a real 401, or as a 422 envelope
   * with the 401 nested under `data` (e.g. { status: 422, data: { status: 401 } }).
   */
  private isSessionExpired(error: HttpErrorResponse, normalizedMessage: string): boolean {
    const nested = error.error?.data;

    if (error.status === 401 || nested?.status === 401) {
      return true;
    }

    if (typeof nested?.name === 'string' && nested.name.toLowerCase() === 'unauthorized') {
      return true;
    }

    return SESSION_EXPIRED_MESSAGES.some(fragment => normalizedMessage.includes(fragment));
  }

  /**
   * Show a single toast and send the user back to sign-in, even when several
   * in-flight requests fail with the same expired token.
   */
  private handleSessionExpiry(): void {
    if (this.isHandlingSessionExpiry) {
      return;
    }
    this.isHandlingSessionExpiry = true;

    this.toast.Error('Session expired. Please login again.');

    setTimeout(() => {
      // logout() clears storage and navigates to /signin
      Promise.resolve(this.auth.logout()).finally(() => {
        this.isHandlingSessionExpiry = false;
      });
    }, 500);
  }

  loaderStart() {
    try {
      // this.ngxService.start();
    } catch (error) {}
  }
  loaderDismiss() {
    try {
      // this.loadingCntr.dismiss();
      // this.ngxService.stop();
    } catch (error) {}
  }
}
